/**
 * @fileoverview UserAvatar - Reusable user avatar with initials fallback
 *
 * Purpose: Display a user's avatar image with automatic initials-based fallback
 * when no image is available. Uses the shadcn Avatar primitive underneath.
 *
 * Dependencies:
 * - @/ui/primitives (Avatar, AvatarImage, AvatarFallback)
 *
 * Used by:
 * - Navigation bar (personal account dropdown, user menu)
 * - Settings page (avatar display)
 * - Organisation members table
 *
 * @example
 * <UserAvatar
 *   avatarUrl={user.avatarUrl}
 *   firstName={user.firstName}
 *   lastName={user.lastName}
 *   email={user.email}
 *   className="h-8 w-8"
 * />
 */

import { Avatar, AvatarImage, AvatarFallback } from "@/ui/primitives";
import { cn } from "@/utils/misc";
import { getDisplayName, getInitials } from "./user-avatar.helpers";

interface UserAvatarProps {
  /** URL of the user's avatar image */
  avatarUrl?: string;
  /** User's first name (used for initials) */
  firstName?: string;
  /** User's last name (used for initials) */
  lastName?: string;
  /** User's email (fallback for initials and alt text) */
  email?: string;
  /** Additional CSS classes for the avatar container */
  className?: string;
  /** Additional CSS classes for the initials fallback (e.g. text size) */
  fallbackClassName?: string;
}

/**
 * User avatar with automatic initials fallback.
 *
 * Shows the user's profile image when available. Falls back to a circle
 * with the user's initials derived from their name or email.
 */
export function UserAvatar({
  avatarUrl,
  firstName,
  lastName,
  email,
  className,
  fallbackClassName,
}: UserAvatarProps) {
  const displayName = getDisplayName(firstName, lastName, email);
  const initials = getInitials(firstName, lastName, email);

  return (
    <Avatar className={cn("h-8 w-8", className)}>
      {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
      <AvatarFallback
        className={cn(
          "bg-[hsl(128,38%,17%)] text-xs font-medium text-white",
          fallbackClassName,
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
